// DocumentFrame — a document opened on the Studio desk, zoomed in (pen
// "Studio – the desk", 4): one frame for every kind. Left, the structure it
// was opened from (DeskStructure.tsx: the course or module, any row opens in
// place); top, the breadcrumb, previous / next and Close (Esc); then the
// title, the AI marker (materials only) and the kind; then the brief box
// (DeskBriefs.tsx: Edit while planned, Generate); then the middle, which is
// the kind's own view (`middle`, rendered by the host: the editor, a
// source's original, a file's facts).
//
// Pure: what is in focus is projected in; Close, the breadcrumb, Esc and
// opening another row go out as intents.

export { DocumentFrame };

import { useLingui } from "@lingui/react/macro";
import { type ReactNode, useEffect, useRef } from "react";
import { AiMarker } from "../AiMarker/AiMarker.js";
import { Icon, type IconName } from "../Icon/Icon.js";
import { IconButton } from "../IconButton/IconButton.js";
import { Kbd } from "../Kbd/Kbd.js";
import type { ContainerCard } from "../Studio/studioCards.js";
import type { DeskDocument } from "../Studio/studioDesk.js";
import type { Point, Size } from "../Studio/studioLayout.js";
import { BriefBox } from "./DeskBriefs.js";
import { type DeskCrumb, DeskCrumbs } from "./DeskCrumbs.js";
import { DeskStructure, structureRows } from "./DeskStructure.js";
import { type DeskNote, docStateOf } from "./deskFacts.js";
import { deskKeyDown } from "./deskKeys.js";

type DocumentFrameIntents = {
    readonly onClose: () => void;
    // The breadcrumb: back to the level of `id` (null: the desk).
    readonly onCrumb: (id: string | null) => void;
    // Another row of the structure: a document, or a module of the course.
    readonly onOpen: (id: string) => void;
    readonly onRevise: (brief: string) => void;
    readonly onGenerate: () => void;
};

// Narrower than this, the structure folds away (previous / next stay).
const STRIP_ROOM = 700;

const iconOf = (doc: DeskDocument): IconName => {
    switch (doc.kind) {
        case "material":
            return doc.template === "quiz" || doc.template === "question"
                ? "mcq"
                : "material";
        case "source":
            return "source";
        case "other":
            return "file";
        default:
            return "file";
    }
};

const DocumentFrame = ({
    doc,
    root,
    project,
    crumbs,
    at,
    size,
    middle,
    online,
    note,
    intents,
}: {
    readonly doc: DeskDocument;
    // The course or module the path starts from (null: opened from the
    // desk).
    readonly root: ContainerCard | null;
    // The project's name, and the levels above it, from the outermost down.
    readonly project: string;
    readonly crumbs: readonly DeskCrumb[];
    readonly at: Point;
    readonly size: Size;
    readonly middle: ReactNode;
    readonly online: boolean;
    // What came of a generate or a brief change.
    readonly note: DeskNote | undefined;
    readonly intents: DocumentFrameIntents;
}): ReactNode => {
    const { t } = useLingui();
    const self = useRef<HTMLElement>(null);
    // Opened: keyboard focus moves into it, so Esc closes it.
    useEffect(() => {
        self.current?.focus({ preventScroll: true });
    }, []);
    const rows = root === null ? [] : structureRows(root);
    const docs = rows.filter((r) => r.type === "document");
    const index = docs.findIndex((r) => r.id === doc.id);
    const previous = docs[index - 1];
    const next = index < 0 ? undefined : docs[index + 1];
    // Its own crumb ("1.3"), or its title.
    const label = docs[index]?.number ?? doc.title;
    const kindLabel = ((): string => {
        switch (doc.kind) {
            case "material":
                return doc.template === "quiz"
                    ? t`Quiz`
                    : doc.template === "question"
                      ? t`Question`
                      : t`Document`;
            case "source":
                return t`Source`;
            case "other":
                return t`File`;
            default:
                return t`File`;
        }
    })();
    return (
        <section
            ref={self}
            role="dialog"
            aria-label={doc.title}
            tabIndex={-1}
            className="bilda-desk__panel bilda-desk__frame"
            data-kind="document"
            data-desk-id={doc.id}
            style={{
                left: at.x,
                top: at.y,
                width: size.width,
                height: size.height,
            }}
            onKeyDown={deskKeyDown(intents.onClose, {
                previous:
                    previous === undefined
                        ? undefined
                        : () => intents.onOpen(previous.id),
                next:
                    next === undefined
                        ? undefined
                        : () => intents.onOpen(next.id),
            })}
        >
            {root === null || size.width < STRIP_ROOM ? null : (
                <DeskStructure
                    root={root}
                    rows={rows}
                    current={doc.id}
                    onOpen={intents.onOpen}
                />
            )}
            <div className="bilda-desk__page">
                <header className="bilda-desk__topbar">
                    <DeskCrumbs
                        project={project}
                        crumbs={crumbs}
                        current={label}
                        onCrumb={intents.onCrumb}
                    />
                    {root === null ? null : (
                        <span className="bilda-desk__flip">
                            <IconButton
                                label={t`Previous document`}
                                size="sm"
                                disabled={previous === undefined}
                                onClick={() =>
                                    previous === undefined
                                        ? undefined
                                        : intents.onOpen(previous.id)
                                }
                            >
                                <Icon name="chevron-up" />
                            </IconButton>
                            <IconButton
                                label={t`Next document`}
                                size="sm"
                                disabled={next === undefined}
                                onClick={() =>
                                    next === undefined
                                        ? undefined
                                        : intents.onOpen(next.id)
                                }
                            >
                                <Icon name="chevron-down" />
                            </IconButton>
                        </span>
                    )}
                    <button
                        type="button"
                        className="bilda-desk__close"
                        onClick={intents.onClose}
                    >
                        {t`Close`}
                        <Kbd
                            className="bilda-desk__esc"
                            keys={["Esc"]}
                            aria-hidden="true"
                        />
                    </button>
                </header>
                <div className="bilda-desk__doc-head">
                    <Icon name={iconOf(doc)} size="md" />
                    <h2 className="bilda-desk__doc-heading" title={doc.title}>
                        {doc.title}
                    </h2>
                    {doc.kind === "material" && doc.aiAuthored ? (
                        <AiMarker variant="chip" />
                    ) : null}
                    <span className="bilda-desk__doc-kind">{kindLabel}</span>
                </div>
                {doc.kind === "material" ? (
                    <BriefBox
                        key={doc.id}
                        brief={doc.document?.brief}
                        state={docStateOf(doc)}
                        note={note}
                        online={online}
                        onRevise={intents.onRevise}
                        onGenerate={intents.onGenerate}
                    />
                ) : null}
                <div className="bilda-desk__middle">{middle}</div>
            </div>
        </section>
    );
};
